import { useState } from "react";
import { Stethoscope, CheckCircle2, XCircle, Clock, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useSickLeaves,
  useVerifySickLeave,
  useRejectSickLeave,
  useDeleteSickLeave,
  useSickLeaveProxyTargets,
} from "../hooks/useLeaves";
import { EmployeePickerContent } from "../components/EmployeePickerContent";
import { ProxySickLeaveSubmitModal } from "../components/ProxySickLeaveSubmitModal";
import { SICK_LEAVE_STATUS_COLORS, type SickLeave } from "../types";

const CAN_DELETE_ROLES = ["accountant", "director"];
// Mirrors SickLeaveViewSet._CAN_VERIFY_ROLES — the roles that may file an L4 on behalf.
const CAN_PROXY_ROLES = ["hr", "accountant", "director"];

type StatusFilter = "all" | "pending" | "verified" | "rejected";

export function SickLeaveReviewPage() {
  const { user } = useAuthStore();
  const canDelete = CAN_DELETE_ROLES.includes(user?.role ?? "");
  const canProxy = CAN_PROXY_ROLES.includes(user?.role ?? "");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const params = statusFilter !== "all" ? { status: statusFilter } : undefined;
  const { data: sickLeavesRes, isLoading } = useSickLeaves(params);
  const records = sickLeavesRes?.data ?? [];

  const verifySickLeave = useVerifySickLeave();
  const rejectSickLeave = useRejectSickLeave();
  const deleteSickLeave = useDeleteSickLeave();

  const [verifyModal, setVerifyModal] = useState<SickLeave | null>(null);
  const [verifyComment, setVerifyComment] = useState("");
  const [rejectModal, setRejectModal] = useState<SickLeave | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectErr, setRejectErr] = useState<string | null>(null);

  // On-behalf submission: employee picker → L4 form for the chosen employee.
  const { data: proxyTargets = [] } = useSickLeaveProxyTargets(canProxy);
  const [proxyPickerOpen, setProxyPickerOpen] = useState(false);
  const [proxySearch, setProxySearch] = useState("");
  const [proxyEmployee, setProxyEmployee] = useState<{ id: string; name: string } | null>(null);

  const handleVerify = () => {
    if (!verifyModal) return;
    verifySickLeave.mutate(
      { id: verifyModal.id, comment: verifyComment },
      { onSuccess: () => { setVerifyModal(null); setVerifyComment(""); } }
    );
  };

  const handleReject = () => {
    if (!rejectModal) return;
    if (!rejectComment.trim()) { setRejectErr("Komentarz jest wymagany."); return; }
    rejectSickLeave.mutate(
      { id: rejectModal.id, comment: rejectComment },
      {
        onSuccess: () => { setRejectModal(null); setRejectComment(""); setRejectErr(null); },
        onError: () => setRejectErr("Błąd odrzucania."),
      }
    );
  };

  const pendingCount = records.filter((r) => r.status === "pending").length;

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            L4 do zatwierdzenia
          </h1>
          <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Weryfikacja zwolnień lekarskich pracowników w systemie ZUS.
          </p>
          <p className="text-xs text-[var(--color-primary)] font-medium mt-0.5">
            {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
              ? "Wszystkie oddziały"
              : user?.branch?.name
              ? `Oddział: ${user.branch.name}`
              : null}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {statusFilter === "pending" && pendingCount > 0 && (
            <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-3 py-1 rounded-full font-medium">
              {pendingCount} {pendingCount === 1 ? "oczekuje" : "oczekują"}
            </span>
          )}
          {canProxy && proxyTargets.length > 0 && (
            <Button variant="secondary" onClick={() => { setProxySearch(""); setProxyPickerOpen(true); }}>
              <UserPlus size={14} className="mr-1.5" /> Złóż L4 pracownika
            </Button>
          )}
        </div>
      </div>

      {/* Status filter */}
      <div className="flex gap-1 mb-5 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit">
        {(["pending", "verified", "rejected", "all"] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === s
                ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {s === "pending" ? "Oczekujące" : s === "verified" ? "Zweryfikowane" : s === "rejected" ? "Odrzucone" : "Wszystkie"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : records.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <Stethoscope size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak zgłoszeń L4 w tej kategorii.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="l4-review-list">
          {records.map((sl) => (
            <div key={sl.id} className="bg-white dark:bg-gray-800 px-4 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
                      {sl.employee_name}
                    </span>
                    <Badge color={SICK_LEAVE_STATUS_COLORS[sl.status]}>
                      {sl.status === "verified" && <CheckCircle2 size={11} className="mr-1" />}
                      {sl.status === "rejected" && <XCircle size={11} className="mr-1" />}
                      {sl.status === "pending" && <Clock size={11} className="mr-1" />}
                      {sl.status_display}
                    </Badge>
                    {/* Self-filed L4s carry the employee as submitted_by — only flag proxy filings. */}
                    {sl.submitted_by && sl.submitted_by !== sl.employee && sl.submitted_by_name && (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                        złożone przez {sl.submitted_by_name}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {formatDate(sl.start_date)} – {formatDate(sl.end_date)}
                    <span className="ml-2 text-gray-400 dark:text-gray-500">
                      ({sl.days_count} {sl.days_count === 1 ? "dzień" : "dni"})
                    </span>
                    <span className="ml-2 text-gray-400 dark:text-gray-500">
                      · zgłoszono {new Date(sl.created_at).toLocaleDateString("pl-PL")}
                    </span>
                  </p>
                  {sl.note && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 italic">{sl.note}</p>
                  )}
                  {sl.verification_comment && (
                    <p className={`text-xs mt-1 font-medium ${sl.status === "rejected" ? "text-red-600" : "text-green-700 dark:text-green-400"}`}>
                      {sl.status === "rejected" ? "Powód odrzucenia: " : "Komentarz weryfikatora: "}
                      {sl.verification_comment}
                    </p>
                  )}
                  {sl.verified_by_name && sl.status !== "pending" && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      Decyzja: {sl.verified_by_name}
                      {sl.verified_at && ` · ${new Date(sl.verified_at).toLocaleString("pl-PL")}`}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                  {sl.status === "pending" && (
                    <>
                      <button
                        onClick={() => { setVerifyModal(sl); setVerifyComment(""); }}
                        className="flex items-center gap-1 text-xs bg-green-600 text-white px-2.5 py-1.5 rounded-md hover:bg-green-700 font-medium"
                      >
                        <CheckCircle2 size={12} /> Zweryfikuj w ZUS
                      </button>
                      <button
                        onClick={() => { setRejectModal(sl); setRejectComment(""); setRejectErr(null); }}
                        className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 font-medium"
                      >
                        <XCircle size={12} /> Odrzuć
                      </button>
                    </>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => {
                        if (confirm("Usunąć to zgłoszenie L4?")) deleteSickLeave.mutate(sl.id);
                      }}
                      className="text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 hover:underline mt-1"
                    >
                      Usuń
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!verifyModal} onClose={() => setVerifyModal(null)} title="Weryfikacja L4 w ZUS">
        <div className="flex flex-col gap-4">
          {verifyModal && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 dark:text-green-300">{verifyModal.employee_name}</p>
              <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                {formatDate(verifyModal.start_date)} – {formatDate(verifyModal.end_date)}
                <span className="ml-1">({verifyModal.days_count} {verifyModal.days_count === 1 ? "dzień" : "dni"})</span>
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={verifyComment}
              onChange={(e) => setVerifyComment(e.target.value)}
              placeholder="Np. ZUS-ZLA nr 12345 — potwierdzono w PUE ZUS..."
            />
          </div>
          <div className="flex gap-3">
            <Button loading={verifySickLeave.isPending} onClick={handleVerify}>
              <CheckCircle2 size={14} className="mr-1.5" /> Potwierdź weryfikację
            </Button>
            <Button variant="secondary" onClick={() => setVerifyModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzucenie L4">
        <div className="flex flex-col gap-4">
          {rejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{rejectModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {formatDate(rejectModal.start_date)} – {formatDate(rejectModal.end_date)}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odrzucenia <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Np. brak zaświadczenia w ZUS PUE..."
            />
            {rejectErr && <p className="text-xs text-red-600 mt-1">{rejectErr}</p>}
          </div>
          <div className="flex gap-3">
            <Button variant="secondary" loading={rejectSickLeave.isPending} onClick={handleReject}>
              <XCircle size={14} className="mr-1.5" /> Odrzuć L4
            </Button>
            <Button variant="secondary" onClick={() => setRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Proxy submit — employee picker */}
      <Modal
        open={proxyPickerOpen}
        onClose={() => { setProxyPickerOpen(false); setProxySearch(""); }}
        title="Złóż L4 — wybierz pracownika"
        size="lg"
      >
        <EmployeePickerContent
          targets={proxyTargets}
          search={proxySearch}
          onSearchChange={setProxySearch}
          onSelect={(t) => { setProxyEmployee({ id: t.id, name: t.full_name }); setProxyPickerOpen(false); setProxySearch(""); }}
          onCancel={() => { setProxyPickerOpen(false); setProxySearch(""); }}
        />
      </Modal>

      {/* Proxy submit — L4 form on behalf of the chosen employee */}
      {proxyEmployee && (
        <ProxySickLeaveSubmitModal
          open={!!proxyEmployee}
          onClose={() => setProxyEmployee(null)}
          employee={proxyEmployee}
        />
      )}
    </div>
  );
}
